import { useAtomValue } from 'jotai'

import type { SubTimer } from '~shared/rundown/cue'
import {
  isSubTimerRunning,
  selectOutputSubTimers,
} from '~shared/rundown/sub-timer-run'

import { runnerAtom, timeOffsetAtom } from '@/store/runner.store'

import { useSubTimerClock } from './sub-timers.hooks'
import { useSubTimerRuns } from './sub-timers.store'
import { getSubTimerView } from './sub-timers.view'
import type { SubTimerView } from './sub-timers.view'

export type OutputSubTimer = {
  id: string
  name: string
  view: SubTimerView
}

/**
 * One cue's sub timers set to show on output, whatever their state. Empty when
 * none are set, which is the cue to fall back to the cue countdown.
 *
 * Ticks only while one of them is counting.
 */
export function useCueOutputSubTimers(
  cueId: string,
  subTimers: readonly SubTimer[],
): OutputSubTimer[] {
  const runs = useSubTimerRuns()
  const runner = useAtomValue(runnerAtom)
  const timeOffset = useAtomValue(timeOffsetAtom)

  const timers = selectOutputSubTimers([{ id: cueId, subTimers }], runs, runner)
  const now = useSubTimerClock(timeOffset, (at) =>
    timers.some((timer) => isSubTimerRunning(timer, at)),
  )

  return timers.map((timer) => ({
    id: timer.id,
    name: timer.name,
    view: getSubTimerView(timer, now),
  }))
}
